import { Scale } from '@/services/canvas/Scale';
import useShapeUtils from '@/modules/Node/utils/shapeUtils';
import type {
	EditingStatus, EdgeReference, DrawContexts, DrawArea,
	SizerPoint, MousePosition,
} from '@/modules/Node';
import NodeTemplate from './NodeTemplate';
import type { NodeTemplateConfig } from './NodeTemplate';
import { getMapAreaService } from '@/modules/map-editor/composables/useMapArea';
import { drawId, drawEditing, drawVisibilityMask, drawHiddenMask, drawConnectionReadyStatus } from '../services/NodeOverlayRenderer';
import { drawSelected } from '../services/NodeSelectionRenderer';
import { checkHovered as hitCheckHovered, checkSizerPointHovered as hitCheckSizerPoint } from '../services/NodeHitDetectionService';

const { addShapeToContext } = useShapeUtils();

export interface NodeConfig extends NodeTemplateConfig {
	x: number;
	y: number;
	template: string | null;
	edgesFrom: EdgeReference[];
	edgesTo: EdgeReference[];
	visibilityMin: number;
	visibilityMax: number;
	hidden: boolean;
	hiddenLocal: boolean;
	locked: boolean;
	connectSign: boolean;
	editingContent: EditingStatus | false;
	editingAppearance: EditingStatus | false;
	availableForConnection: boolean;
	contentBlocked: boolean;
	contentLinkBlocked: boolean;
}

export default class Node extends NodeTemplate {
	x: number;
	y: number;
	template: string | null;
	visibilityMin: number;
	visibilityMax: number;
	hidden: boolean;
	hiddenLocal: boolean;
	locked: boolean;
	connectSign: boolean;
	edgesFrom: EdgeReference[];
	edgesTo: EdgeReference[];
	contentBlocked: boolean;
	contentLinkBlocked: boolean;
	declare editingContent: EditingStatus | false;
	declare editingAppearance: EditingStatus | false;
	availableForConnection: boolean;
	pathInteraction: Path2D | null;
	_lastPathMapX: number | null;
	_lastPathMapY: number | null;
	_lastPathNodeX: number | null;
	_lastPathNodeY: number | null;
	_lastPathZoom: number | null;
	_lastPathCanvasW: number | null;
	currentLayerId: number;
	declare ZOOM_MAX: number;
	selectedPoints: SizerPoint[];
	selectedPoint: SizerPoint | null;
	declare multiSelected: boolean;

	constructor(
		{
			x, y, template, shape, edgesFrom, edgesTo,
			radius, width, height, rotate,
			border, borderColor, borderStyle, borderWidth,
			surface, bgColor, shadow, shadowColor, shadowX, shadowY, shadowBlur,
			title, titlePosition, textAlign, fontSize, fontColor, fontFamily, fontWeight, fontStyle, lineHeight,
			mediaCropEnabled, mediaSize, mediaOpacity, media, mediaURL, image, gifAnimation,
			id,
			visibilityMin, visibilityMax, hidden, hiddenLocal, locked, connectSign,
			editingContent, editingAppearance, availableForConnection,
			contentBlocked, contentLinkBlocked
		}: NodeConfig)
	{
		super({
			shape, id,
			radius, width, height, rotate,
			border, borderColor, borderStyle, borderWidth,
			surface, bgColor, shadow, shadowColor, shadowX, shadowY, shadowBlur,
			title, titlePosition, textAlign, fontSize, fontColor, fontFamily, fontWeight, fontStyle, lineHeight,
			mediaCropEnabled, mediaSize, mediaOpacity, media, mediaURL, image, gifAnimation
		});
		this.x = x;
		this.y = y;

		this.template = template;
		this.visibilityMin = visibilityMin;
		this.visibilityMax = visibilityMax;

		this.hidden = hidden;
		this.hiddenLocal = hiddenLocal;
		this.locked = locked;
		this.connectSign = connectSign;

		this.edgesFrom = edgesFrom;
		this.edgesTo = edgesTo;

		this.contentBlocked = contentBlocked;
		this.contentLinkBlocked = contentLinkBlocked;

		this.editingContent = editingContent;
		this.editingAppearance = editingAppearance;
		this.availableForConnection = availableForConnection;

		this.pathInteraction = null;
		this._lastPathMapX = null;
		this._lastPathMapY = null;
		this._lastPathNodeX = null;
		this._lastPathNodeY = null;
		this._lastPathZoom = null;
		this._lastPathCanvasW = null;

		this.currentLayerId = 1;
		this.ZOOM_MAX = 1.62854;

		this.selectedPoints = [];
		this.selectedPoint = null;
	}

	// CACHE
	cache(layerID: number | string, ignoreMedia: boolean = false): void {
		this.currentLayerId = parseInt(String(layerID));
		this.globalAlpha = this.getVisibility(layerID);

		// get coords of shape's center
		({x: this.xCache, y: this.yCache} = super._getShapeCoordinates());

		if(this.availableForConnection){
			if(this.editingContent || this.editingAppearance) this._drawEditing();
			super._drawShape();
			if(!ignoreMedia) super._drawMedia();
			super._drawStroke();

			this._drawVisibilityMask();
			if(this.hidden) this._drawHiddenMask();
		}

		this._drawId();
		if(this.fontSize !== null && this.fontSize > 0 && this.availableForConnection) super._drawText();

		if(!this.availableForConnection) this._drawConnectionReadyStatus();

	}

	cacheUpdate(layerID: number | string, ignoreMedia: boolean = false): void {
		super.cacheClear();
		// Invalidate interaction path so it rebuilds on next draw
		this._lastPathZoom = null;
		this.cache(layerID, ignoreMedia);
	}

	// DRAWS
	draw(contexts: DrawContexts, zoomLevel: number, ignoreMapArea: boolean = false, area: DrawArea = {width: null, height: null, shiftX: 0, shiftY: 0}): void {
		if(this.hiddenLocal) return;

		const {ctxBuffered: ctx, ctxHighlights} = contexts;
		const mas = getMapAreaService();
		const x = mas.x;
		const y = mas.y;
		// to draw cacheInstance to ctx of the mapArea by computed coordinates
		// ignore map area used for getting snapshot of the map
		const {width, height, shiftX, shiftY} = area;
		const shiftMapX = ignoreMapArea ? width! / 2 + Math.abs(shiftX)  : x;
		const shiftMapY = ignoreMapArea ? height! / 2 + Math.abs(shiftY) : y;

		const computedX = Scale.x(this.x - this.xCache! + shiftMapX, ctx.canvas.width / 2, zoomLevel);
		const computedY = Scale.y(this.y - this.yCache! + shiftMapY, ctx.canvas.height / 2, zoomLevel);
		const ZOOM_FACTOR = this.ZOOM_MAX * this.pixelRatio!;

		if(!this.cacheInstance!.width || !this.cacheInstance!.height) return;
		ctx.drawImage(this.cacheInstance!, computedX, computedY, (this.cacheInstance!.width / ZOOM_FACTOR) * zoomLevel, (this.cacheInstance!.height / ZOOM_FACTOR) * zoomLevel);

		// Rebuild interaction path only when position/zoom/canvas actually changed
		const canvasW = ctx.canvas.width;
		if (this._lastPathMapX !== x || this._lastPathMapY !== y ||
			this._lastPathNodeX !== this.x || this._lastPathNodeY !== this.y ||
			this._lastPathZoom !== zoomLevel || this._lastPathCanvasW !== canvasW) {
			this._lastPathMapX = x;
			this._lastPathMapY = y;
			this._lastPathNodeX = this.x;
			this._lastPathNodeY = this.y;
			this._lastPathZoom = zoomLevel;
			this._lastPathCanvasW = canvasW;
			this.pathInteraction = new Path2D();
			addShapeToContext({
				ctx: this.pathInteraction,
				node: this,
				center: {
					x: Scale.x(this.x + x, canvasW / 2, zoomLevel),
					y: Scale.y(this.y + y, ctx.canvas.height / 2, zoomLevel)
				},
				zoomLevel
			});
		}

		// draw selected
		if(this.selected) this._drawSelected(ctxHighlights, zoomLevel);
	}

	_drawSelected(ctx: CanvasRenderingContext2D, zoomLevel: number): void {
		this.selectedPoints = drawSelected(ctx, this, zoomLevel);
	}

	_drawId(): void {
		drawId(this.ctx!, this);
	}

	_drawEditing(): void {
		drawEditing(this.ctx!, this);
	}

	_drawConnectionReadyStatus(): void {
		drawConnectionReadyStatus(this.ctx!, this);
	}

	_drawVisibilityMask(): void {
		drawVisibilityMask(this.ctx!, this);
	}

	_drawHiddenMask(): void {
		drawHiddenMask(this.ctx!, this);
	}

	setSelected(status: boolean, multiSelected: boolean = false): void {
		this.selected = status;
		this.multiSelected = multiSelected;
	}

	checkHovered(ctxPath: CanvasRenderingContext2D, mouse: MousePosition): boolean | null {
		const result = hitCheckHovered(ctxPath, this.pathInteraction, mouse, this.hiddenLocal);
		this.hover = result ?? false;
		return result;
	}

	checkSizerPointHovered(mouse: MousePosition, isMobile: boolean = false): SizerPoint | null {
		this.selectedPoint = hitCheckSizerPoint(this.selectedPoints, mouse, isMobile);
		return this.selectedPoint;
	}

	getVisibility(layerID: number | string): number {
		const id = typeof layerID === 'string' ? parseInt(layerID) : layerID || this.currentLayerId;
		return id < this.visibilityMin || id > this.visibilityMax ? 0.5 : 1;
	}
}
